import React, { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  InfoBar,
  isTouchDevice,
  type ManagedModalComponentProps,
  TextField,
} from "@shapr3d/frontend-lib";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { maxVersionNameLength } from "../../config";
import useTranslation from "../../hooks/useTranslation";
import { Modal } from "../../lib/modal";

import style from "./RenameVersionModal.module.css";

type RenameVersionFormData = {
  versionName: string;
};

interface RenameVersionModalProps {
  modalProps: ManagedModalComponentProps;
  versionName?: string;
  isPending?: boolean;
  isError?: boolean;
  onSave: (name: string) => void;
}

export const RenameVersionModal = React.forwardRef<HTMLElement, RenameVersionModalProps>(
  ({ modalProps, versionName, isPending = false, isError = false, onSave }, ref) => {
    const { t, m } = useTranslation();

    const schema = useMemo(
      () =>
        z.object({
          versionName: z
            .string()
            .trim()
            .min(1, t(m.ProjectSidebar.RenameVersionValidationRequired))
            .max(
              maxVersionNameLength,
              t(m.ProjectSidebar.RenameVersionValidationMaxLength, { max: maxVersionNameLength })
            ),
        }),
      [t, m]
    );

    const {
      register,
      handleSubmit,
      formState: { errors, isSubmitting },
      reset,
    } = useForm<RenameVersionFormData>({
      resolver: zodResolver(schema),
      defaultValues: { versionName: versionName },
    });

    useEffect(() => {
      if (modalProps.open) {
        reset({ versionName: versionName });
      }
    }, [modalProps.open, versionName, reset]);

    const submit = (data: RenameVersionFormData) => {
      onSave(data.versionName);
    };

    const isSaving = isPending || isSubmitting;

    return (
      <Modal
        {...modalProps}
        size="medium"
        title={t(m.ProjectSidebar.RenameVersionModalTitle)}
        telemetryName="rename_version"
        ref={ref}
      >
        <form className={style.renameVersionForm} onSubmit={handleSubmit(submit)}>
          <TextField
            {...register("versionName")}
            placeholder={t(m.ProjectSidebar.RenameVersionModalPlaceholder)}
            errorMessage={errors.versionName?.message}
            maxLength={maxVersionNameLength}
            disabled={isSaving}
            autoFocus={!isTouchDevice()}
            autoComplete="off"
          />
          {isError && <InfoBar variant="critical" title={t(m.ProjectSidebar.RenameVersionError)} />}
          <div className={style.actions}>
            <Button variant="grayscale" onClick={modalProps.onClose} disabled={isSaving}>
              {t(m.ProjectSidebar.Cancel)}
            </Button>
            <Button type="submit" disabled={isSaving}>
              {t(m.ProjectSidebar.Save)}
            </Button>
          </div>
        </form>
      </Modal>
    );
  }
);

RenameVersionModal.displayName = "RenameVersionModal";
